import React, {useState} from "react";
import {
    Box,
    Button,
    Checkbox,
    InputAdornment,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Popover,
    Radio,
    TextField,
    Typography
} from "@mui/material";
import {Add, ArrowDropDown, Search} from "@mui/icons-material";
import i18n from "../../../i18n/i18n";

const t = (k: string, o?: Record<string, unknown>) => i18n.t(`filters.${k}`, {ns: 'sit', ...o});

/**
 * One filter of a SIT filter bar: a "Label: Any" chip whose popover edits the draft. `primary` filters always show;
 * the others are under "Add filter" until they are added or set.
 */
export type SitFilterDef = {
    id: string;
    label: string;
    primary?: boolean;
    /** Set to something other than its default, in the draft. */
    active: boolean;
    /** "Any", the one selected value, or "n selected". */
    status: string;
    render: (close: () => void) => React.ReactNode;
};

type Option<V> = {value: V; label: string};

/**
 * The options of a filter chip: checkboxes (multiple) or a single choice, with a search box for long lists.
 * With `excluding`, a multi-select also offers Exclude on the hovered option (as in Spend Landscape): the filter then
 * means everything except `selected`, shown as the options that stay checked.
 */
export function SitOptionList<V extends string | number>({options, selected, multiple, excluding, onChange}: {
    options: Option<V>[];
    selected: V[];
    multiple?: boolean;
    /** Excluding mode of an excludable multi-select; leave undefined for include-only filters. */
    excluding?: boolean;
    onChange: (selected: V[], excluding: boolean) => void;
}) {
    const [search, setSearch] = useState('');
    const shown = options.filter(o => !search || o.label.toLowerCase().includes(search.toLowerCase()));
    const excludable = Boolean(multiple) && excluding !== undefined;
    const toggle = (v: V) => {
        const next = selected.includes(v) ? selected.filter(s => s !== v) : [...selected, v];
        // Excluding nothing means no filter: back to include mode.
        onChange(next, Boolean(excluding) && next.length > 0);
    };
    return (
        <Box className="sit-filter-options">
            {options.length > 8 &&
                <TextField autoFocus fullWidth size="small" placeholder={t('search')} value={search}
                           onChange={e => setSearch(e.target.value)}
                           slotProps={{input: {startAdornment: <InputAdornment position="start"><Search fontSize="small"/></InputAdornment>}}}/>}
            <List dense>
                {shown.map(o => {
                    const on = selected.includes(o.value);
                    const checked = excluding ? !on : on;
                    return (
                        <ListItem key={String(o.value)} disablePadding className="sit-filter-option"
                                  secondaryAction={excludable &&
                                      <Button size="small" className="sit-filter-exclude"
                                              onClick={() => onChange([o.value], true)}>
                                          {t('exclude')}
                                      </Button>}>
                            <ListItemButton selected={!multiple && on}
                                            onClick={() => multiple ? toggle(o.value) : onChange([o.value], false)}>
                                <ListItemIcon sx={{minWidth: 36}}>
                                    {multiple ? <Checkbox edge="start" size="small" checked={checked} tabIndex={-1}/>
                                        : <Radio edge="start" size="small" checked={on} tabIndex={-1}/>}
                                </ListItemIcon>
                                <ListItemText primary={o.label}/>
                            </ListItemButton>
                        </ListItem>
                    );
                })}
                {shown.length === 0 && <Typography variant="body2" color="text.secondary" sx={{p: 1}}>{t('noOptions')}</Typography>}
            </List>
        </Box>
    );
}

/** A "Label: Any" chip that opens the filter's options. */
const SitFilterChip: React.FC<{def: SitFilterDef}> = ({def}) => {
    const [anchor, setAnchor] = useState<HTMLElement | null>(null);
    const close = () => setAnchor(null);
    return <>
        <Button className={'sit-filter-chip' + (def.active ? ' active' : '')} variant="outlined" disableElevation
                endIcon={<ArrowDropDown/>} aria-haspopup="dialog" aria-expanded={Boolean(anchor)}
                onClick={e => setAnchor(e.currentTarget)}>
            <span className="sit-filter-chip-label">{def.label}:</span>&nbsp;<b>{def.status}</b>
        </Button>
        <Popover open={Boolean(anchor)} anchorEl={anchor} onClose={close}
                 anchorOrigin={{vertical: 'bottom', horizontal: 'left'}}
                 transformOrigin={{vertical: 'top', horizontal: 'left'}}>
            {anchor && def.render(close)}
        </Popover>
    </>;
};

type Props = {
    filters: SitFilterDef[];
    /** The draft differs from the applied filters. */
    dirty: boolean;
    /** Some applied or draft filter is set, so Clear all has something to clear. */
    clearable: boolean;
    onApply: () => void;
    onClear: () => void;
    /** Shown before the chips (e.g. the search box). */
    lead?: React.ReactNode;
    /** Shown at the end of the bar (e.g. the add button). */
    trail?: React.ReactNode;
    className?: string;
};

/**
 * SIT filter bar, after the Spend Landscape bar (Mithra 3.0): chips for the main filters, "Add filter" for the rest,
 * Clear all, and Apply, which loads the collected changes together.
 */
export const SitFilterBar: React.FC<Props> = ({filters, dirty, clearable, onApply, onClear, lead, trail, className}) => {
    const [added, setAdded] = useState<string[]>([]);
    const [addAnchor, setAddAnchor] = useState<HTMLElement | null>(null);
    const [search, setSearch] = useState('');
    const shown = filters.filter(f => f.primary || f.active || added.includes(f.id));
    const addable = filters.filter(f => !shown.includes(f))
        .filter(f => !search || f.label.toLowerCase().includes(search.toLowerCase()));

    return (
        <div className={'sit-filterbar ' + (className ?? '')}>
            {lead}
            {shown.map(f => <SitFilterChip key={f.id} def={f}/>)}
            {filters.length > shown.length && <>
                <Button className="sit-filter-add" variant="outlined" startIcon={<Add/>}
                        onClick={e => {
                            setSearch('');
                            setAddAnchor(e.currentTarget);
                        }}>
                    {t('add')}
                </Button>
                <Popover open={Boolean(addAnchor)} anchorEl={addAnchor} onClose={() => setAddAnchor(null)}
                         anchorOrigin={{vertical: 'bottom', horizontal: 'left'}}
                         transformOrigin={{vertical: 'top', horizontal: 'left'}}>
                    <Box sx={{width: '18em', p: 1}}>
                        <TextField autoFocus fullWidth size="small" placeholder={t('searchFilters')} value={search}
                                   onChange={e => setSearch(e.target.value)}
                                   slotProps={{input: {startAdornment: <InputAdornment position="start"><Search fontSize="small"/></InputAdornment>}}}/>
                        <List dense>
                            {addable.map(f => (
                                <ListItemButton key={f.id} onClick={() => {
                                    setAdded([...added, f.id]);
                                    setAddAnchor(null);
                                }}>
                                    <ListItemText primary={f.label}/>
                                </ListItemButton>
                            ))}
                            {addable.length === 0 &&
                                <Typography variant="body2" color="text.secondary" sx={{p: 1}}>{t('noFilters')}</Typography>}
                        </List>
                    </Box>
                </Popover>
            </>}
            <Button variant="text" className="sit-filter-clear" disabled={!clearable}
                    onClick={() => {
                        setAdded([]);
                        onClear();
                    }}>
                {t('clearAll')}
            </Button>
            <Button variant="contained" disableElevation className="sit-filter-apply" disabled={!dirty} onClick={onApply}>
                {t('apply')}
            </Button>
            {dirty && <Typography variant="caption" color="text.secondary">{t('notApplied')}</Typography>}
            {trail && <><span className="sit-filterbar-spacer"/>{trail}</>}
        </div>
    );
};
